/**
 * CreateCompanyForm Component
 *
 * Form for creating a new company.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { companiesApi } from '@/services/contactsApi';
import type { CreateCompanyRequest } from '@/services/contactsApi';

export default function CreateCompanyForm() {
  const { t } = useTranslation('people');
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState<CreateCompanyRequest>({
    name: '',
    domain: '',
    industry: '',
    website: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    setIsSubmitting(true);
    setError(null);
    try {
      const created = await companiesApi.create(form);
      // Navigate to the new company's detail page
      navigate(`/people/companies/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.failed_to_create', { type: 'company' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/people/companies');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={handleCancel}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('create_company_form.back')}
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{t('create_company_form.title')}</h2>
        <div className="w-16" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('create_company_form.fields.company_name')}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              autoFocus
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_company_form.fields.domain')}
              </label>
              <input
                type="text"
                value={form.domain || ''}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
                placeholder={t('create_company_form.placeholders.domain')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_company_form.fields.industry')}
              </label>
              <input
                type="text"
                value={form.industry || ''}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('create_company_form.fields.website')}
            </label>
            <input
              type="url"
              value={form.website || ''}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder={t('create_company_form.placeholders.website')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('create_company_form.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !form.name.trim()}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('create_company_form.actions.creating') : t('create_company_form.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
